import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import { QuickMatchCard } from "@/components/QuickMatchCard";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { toast } from "sonner";
import { Rocket, Crown, Coins, Shield, ArrowRight, Lock, Phone, UserCheck, UserX, Users, Flame, MessageSquare, Send, X, Camera } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { DeleteAccount } from "@/pages/Safety";

export default function Dashboard() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [online, setOnline] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
    const poll = () => api.get("/presence").then((r) => setOnline(r.data.online)).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono text-sm">Loading…</div>;
  const { user, stats, blocked, history } = data;
  const isPremium = !!user.premium || user.role === "admin" || user.role === "moderator";
  const saveGender = async (g) => {
    try { const { data: u } = await api.post("/preferences", { gender: g }); setData((d) => ({ ...d, user: u })); setUser(u); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen orbit-bg-grid">
      <Nav />
      <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-4">
            <PhotoUploader user={user} onSaved={(u) => { setData((d) => ({ ...d, user: u })); setUser(u); }} />
            <div>
              <div className="text-xs font-mono uppercase text-cyan-300">Your Orbit Hello</div>
              <h1 className="font-display text-4xl font-extrabold mt-1">Hi, {user.display_name} <span className="text-2xl">{user.flag}</span></h1>
              <p className="text-slate-400 mt-1">Ready for your next connection?</p>
            </div>
          </div>
        </div>

        <div className="mb-8 orbit-fade-up"><QuickMatchCard user={user} online={online} isPremium={isPremium} /></div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Rocket} label="Total matches" value={stats.total_matches} />
          <StatCard icon={Flame} label="Daily streak" value={`${stats.streak} day${stats.streak === 1 ? "" : "s"}`} tone={stats.streak > 0 ? "amber" : "slate"}
            sub={stats.streak_today ? `best ${stats.best_streak} · come back tomorrow to keep it` : "chat with someone today to keep it going"} testId="dashboard-streak" />
          <StatCard icon={Crown} label="Plan" value={user.premium ? (user.plan_name || "Galaxy Pass") : "Free"} tone={user.premium ? "fuchsia" : "slate"}
            sub={user.premium && user.plan_expires_at ? `until ${new Date(user.plan_expires_at).toLocaleDateString()}` : null} />
        </div>

        <div className="flex gap-2 border-b border-white/10 mb-6">
          {[
            ["overview", "Overview"],
            ["friends", "Friends"],
            ["history", "History"],
            ["blocked", "Blocked"],
            ["settings", "Settings"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 text-sm border-b-2 transition ${tab === id ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-slate-200"}`} data-testid={`dashboard-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="orbit-glass rounded-2xl p-6">
              <div className="text-xs font-mono uppercase text-slate-500 mb-2">Profile</div>
              <div className="space-y-2 text-sm">
                <Row k="Country" v={`${user.flag || ""} ${user.country || "—"}`} />
                <Row k="Languages" v={(user.languages || []).join(", ") || "—"} />
                <Row k="Age" v={user.age || "—"} />
                <Row k="Interests" v={(user.interests || []).join(", ") || "—"} />
              </div>
            </div>
            <div className="orbit-glass rounded-2xl p-6">
              <div className="text-xs font-mono uppercase text-slate-500 mb-2">Preferences</div>
              <div className="space-y-2 text-sm">
                <Row k="Country filter" v={<CountryFilter user={user} onSaved={(u) => { setData((d) => ({ ...d, user: u })); setUser(u); }} />} />
                <div className="pt-1">
                  <div className="text-slate-500 mb-2">I am</div>
                  <span className="inline-flex flex-wrap gap-1.5" data-testid="dashboard-gender">
                    {[["male", "Man"], ["female", "Woman"], ["nonbinary", "Non-binary"]].map(([g, label]) => (
                      <button key={g} onClick={() => saveGender(g)} className={`orbit-chip text-xs ${user.gender === g ? "active" : ""}`} data-testid={`dashboard-gender-${g}`}>{label}</button>
                    ))}
                  </span>
                </div>
                <div className="pt-1">
                  <div className="text-slate-500 mb-2">Match with</div>
                  <GenderFilter user={user} onSaved={(u) => { setData((d) => ({ ...d, user: u })); setUser(u); }} />
                </div>
              </div>
              {!isPremium && <button onClick={() => nav("/pricing")} className="mt-4 orbit-chip active" data-testid="dashboard-upgrade-btn">Upgrade for gender & country filters →</button>}
            </div>
          </div>
        )}

        {tab === "friends" && <FriendsTab />}

        {tab === "history" && (
          <div className="space-y-2">
            {history.length === 0 && <div className="text-slate-500 text-sm">No conversations yet.</div>}
            {history.map((h) => (
              <div key={h.session_id} className="orbit-glass rounded-xl p-4 flex items-center gap-4">
                <Avatar profile={h.match} size="w-12 h-12" />
                <div className="flex-1">
                  <div className="font-semibold flex items-center gap-2">{h.match.flag} {h.match.name} <span className="text-slate-500 text-xs">· {h.match.country}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${h.kind === "live" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{h.kind === "live" ? "LIVE" : "DEMO MATCH · simulated"}</span>
                  </div>
                  <div className="text-xs text-slate-500">Shared: {(h.shared || []).join(", ") || "—"}</div>
                </div>
                <div className="text-xs font-mono text-slate-500">{new Date(h.started_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "blocked" && (
          <div className="space-y-2">
            {blocked.length === 0 && <div className="text-slate-500 text-sm">You haven't blocked anyone.</div>}
            {blocked.map((b) => (
              <div key={b.id} className="orbit-glass rounded-xl p-4 flex items-center gap-3">
                <span className="text-xl">{b.flag}</span>
                <div className="flex-1"><div className="font-semibold">{b.name}</div><div className="text-xs text-slate-500">{b.country}</div></div>
                <span className="orbit-chip !text-rose-300 !border-rose-500/40 !bg-rose-500/10">Blocked</span>
              </div>
            ))}
          </div>
        )}

        {tab === "settings" && (
          <div className="orbit-glass rounded-2xl p-6 space-y-3">
            <div className="text-sm text-slate-300">Privacy, safety, and account settings. Read our <a href="/safety" className="text-cyan-300 underline" data-testid="settings-safety-link">terms, privacy policy & safety center</a>.</div>
            <DeleteAccount />
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              <button className="orbit-chip !py-3" data-testid="setting-hide-avatar">Hide profile picture</button>
              <button className="orbit-chip !py-3" data-testid="setting-notifications">Notification preferences</button>
              <button className="orbit-chip !py-3" data-testid="setting-download-data">Download my data</button>
              <button className="orbit-chip !py-3 !text-rose-300 !border-rose-500/40" data-testid="setting-delete-account">Delete account</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "slate", sub, testId }) {
  const tones = {
    slate: "text-slate-200",
    cyan: "text-cyan-300",
    emerald: "text-emerald-300",
    fuchsia: "text-fuchsia-300",
    amber: "text-amber-300",
  };
  return (
    <div className="orbit-glass rounded-2xl p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase text-slate-400"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`font-display text-3xl font-bold mt-2 ${tones[tone]}`}>{value}</div>
      {sub && <div className="text-xs font-mono text-slate-500 mt-1" data-testid={testId ? `${testId}-sub` : "dashboard-plan-expiry"}>{sub}</div>}
    </div>
  );
}
const PRESENCE = {
  available: { dot: "bg-emerald-400", text: "text-emerald-300", label: "online · available" },
  busy: { dot: "bg-amber-400", text: "text-amber-300", label: "online · in a call" },
  offline: { dot: "bg-slate-600", text: "text-slate-500", label: "offline" },
};
function presenceOf(f) {
  if (!f.online) return PRESENCE.offline;
  return f.available ? PRESENCE.available : PRESENCE.busy;
}

function Row({ k, v }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-slate-500">{k}</span><span className="text-slate-100 text-right">{v}</span></div>;
}

function PhotoUploader({ user, onSaved }) {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const avatarProfile = React.useMemo(() => ({ ...user, name: user.display_name }), [user]);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSaved(data);
      toast.success("Profile photo updated");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { const { data } = await api.delete("/profile/photo"); onSaved(data); toast("Photo removed"); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="relative group" data-testid="profile-photo">
      <Avatar profile={avatarProfile} size="w-20 h-20" text="text-2xl" className="border-2 border-white/15" testId="profile-photo-img" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-cyan-500 text-slate-950 flex items-center justify-center shadow-lg hover:scale-110 transition disabled:opacity-60" title="Change photo" data-testid="profile-photo-upload-btn">
        {busy ? <span className="w-3 h-3 border-2 border-slate-950/40 border-t-slate-950 rounded-full animate-spin" /> : <Camera className="w-4 h-4" />}
      </button>
      {user.avatar && <button onClick={remove} className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-slate-900 border border-white/10 text-slate-400 hover:text-rose-300 hidden group-hover:flex items-center justify-center" title="Remove photo" data-testid="profile-photo-remove-btn"><X className="w-3 h-3" /></button>}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="profile-photo-input" />
    </div>
  );
}

function FriendsTab() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { friendsVersion, callFriend } = useCall();
  const [list, setList] = useState(null);
  const [thread, setThread] = useState(params.get("thread"));
  const load = () => api.get("/friends").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [friendsVersion]);
  const respond = async (request_id, accept) => {
    try { await api.post("/friends/respond", { request_id, accept }); toast.success(accept ? "Friend added" : "Request declined"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { try { await api.delete(`/friends/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const call = async (f) => { await callFriend(f.id); nav("/chat"); };
  if (!list) return <div className="text-slate-500 text-sm">Loading friends…</div>;
  const threadFriend = list.friends.find((f) => f.id === thread);
  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="friends-tab">
      <div className="md:col-span-2 space-y-2">
        <div className="text-xs font-mono uppercase text-slate-500 mb-2 flex items-center gap-2"><Users className="w-3 h-3" /> Friends · {list.friends.length}</div>
        {list.friends.length === 0 && <div className="orbit-glass rounded-2xl p-6 text-sm text-slate-500">No friends yet. After a great chat, tap "Add as friend" on the recap screen.</div>}
        {list.friends.map((f) => (
          <div key={f.id} className="orbit-glass rounded-xl p-4 flex items-center gap-4" data-testid={`friend-row-${f.id}`}>
            <div className="relative w-11 h-11 shrink-0">
              <Avatar profile={f} />
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-slate-950 ${presenceOf(f).dot}`} />
            </div>
            <div className="flex-1">
              <div className="font-semibold">{f.flag} {f.name}</div>
              <div className="text-xs font-mono" data-testid={`friend-status-${f.id}`}>
                <span className={presenceOf(f).text}>{presenceOf(f).label}</span>
              </div>
            </div>
            <button onClick={() => { setThread(f.id); setList((l) => ({ ...l, friends: l.friends.map((x) => (x.id === f.id ? { ...x, unread: 0 } : x)) })); }} className="relative orbit-chip !px-3 inline-flex items-center gap-1.5" data-testid={`friend-message-${f.id}`}>
              <MessageSquare className="w-4 h-4" /> Message
              {f.unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-fuchsia-500 text-white text-[10px] font-mono rounded-full px-1.5 py-0.5" data-testid={`friend-unread-${f.id}`}>{f.unread}</span>}
            </button>
            <button onClick={() => call(f)} disabled={!f.available} className="orbit-btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid={`friend-call-${f.id}`}><Phone className="w-4 h-4" /> Call</button>
            <button onClick={() => remove(f.id)} className="text-slate-500 hover:text-rose-400 p-2" title="Remove" data-testid={`friend-remove-${f.id}`}><UserX className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        {threadFriend ? (
          <MessageThread friend={threadFriend} onClose={() => { setThread(null); load(); }} />
        ) : (
          <>
            <div>
              <div className="text-xs font-mono uppercase text-slate-500 mb-2">Requests · {list.incoming.length}</div>
              {list.incoming.length === 0 && <div className="text-xs text-slate-600">No pending requests.</div>}
              {list.incoming.map((r) => (
                <div key={r.request_id} className="orbit-glass rounded-xl p-3 flex items-center gap-3 mb-2" data-testid={`friend-request-${r.from.id}`}>
                  <div className="flex-1 text-sm"><span className="font-semibold">{r.from.flag} {r.from.name}</span><div className="text-xs text-slate-500">{r.from.country}</div></div>
                  <button onClick={() => respond(r.request_id, true)} className="orbit-chip !text-emerald-300 !border-emerald-500/40 !px-2" data-testid={`friend-accept-${r.from.id}`}><UserCheck className="w-4 h-4" /></button>
                  <button onClick={() => respond(r.request_id, false)} className="orbit-chip !text-rose-300 !border-rose-500/40 !px-2" data-testid={`friend-decline-${r.from.id}`}><UserX className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {list.outgoing.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase text-slate-500 mb-2">Sent · {list.outgoing.length}</div>
                {list.outgoing.map((r) => <div key={r.request_id} className="text-sm text-slate-400 mb-1" data-testid={`friend-outgoing-${r.to.id}`}>{r.to.flag} {r.to.name} <span className="text-xs text-slate-600">· pending</span></div>)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MessageThread({ friend, onClose }) {
  const { friendsVersion } = useCall();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const endRef = React.useRef(null);
  const load = () => api.get(`/friends/${friend.id}/messages`).then((r) => setMsgs(r.data.messages)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [friend.id, friendsVersion]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const sendIt = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/friends/${friend.id}/messages`, { text: t });
      setMsgs((m) => [...(m || []), data.message]);
      setText("");
      if (!data.delivered_live) toast(`${friend.name} is offline — they'll see it when they're back`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="orbit-glass rounded-2xl flex flex-col h-[420px]" data-testid="message-thread">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <div className="font-display font-semibold text-sm">{friend.flag} {friend.name} <span className={`text-[10px] font-mono ml-1 ${friend.online ? "text-emerald-300" : "text-slate-500"}`}>{friend.online ? "online" : "offline"}</span></div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-100" data-testid="message-thread-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="message-thread-list">
        {msgs === null && <div className="text-xs text-slate-500">Loading…</div>}
        {msgs?.length === 0 && <div className="text-xs text-slate-500 text-center mt-8">Say hi — {friend.name} will see it even if they're offline.</div>}
        {msgs?.map((m) => (
          <div key={m.id} data-testid={`thread-message-${m.mine ? "me" : "them"}`} className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.mine ? "ml-auto bg-cyan-500 text-slate-950" : "bg-slate-800 text-slate-100"}`}>
            {m.text}
            <div className={`text-[10px] font-mono mt-0.5 ${m.mine ? "text-slate-800" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-2 border-t border-white/10 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendIt()} maxLength={500}
          placeholder={`Message ${friend.name}…`} className="flex-1 px-3 py-2 bg-slate-950/60 border border-white/10 rounded-full text-sm outline-none focus:border-cyan-400" data-testid="message-thread-input" />
        <button onClick={sendIt} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-full w-9 h-9 flex items-center justify-center" data-testid="message-thread-send"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function CountryFilter({ user, onSaved }) {
  const nav = useNavigate();
  const [countries, setCountries] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isPremium = !!user.premium || user.role === "admin" || user.role === "moderator";
  useEffect(() => { if (open && !countries.length) api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {}); }, [open, countries.length]);
  const save = async (value) => {
    try {
      const { data } = await api.post("/preferences", { country_preference: value });
      onSaved(data);
      setOpen(false);
      toast.success(value === "worldwide" ? "Matching worldwide" : `Matching with ${value} first`);
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!isPremium) {
    return (
      <button onClick={() => nav("/pricing")} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-fuchsia-300" data-testid="dashboard-country-filter-locked">
        🌍 Worldwide <Lock className="w-3 h-3 text-fuchsia-300" /> <span className="text-[10px] font-mono text-fuchsia-300">VIP</span>
      </button>
    );
  }
  const shown = countries.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <span className="relative inline-block">
      <button onClick={() => setOpen((o) => !o)} className="orbit-chip active text-xs" data-testid="dashboard-country-filter-btn">{user.country_preference === "worldwide" ? "🌍 Worldwide" : user.country_preference} ▾</button>
      {open && (
        <div className="absolute right-0 mt-2 z-30 orbit-glass rounded-2xl p-2 w-64 max-h-72 overflow-y-auto grid gap-1 text-left" data-testid="dashboard-country-filter-menu">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 200+ countries…" className="px-3 py-2 rounded-lg bg-slate-950/70 border border-white/10 text-xs outline-none focus:border-cyan-400 text-slate-100" data-testid="country-filter-search" />
          <button onClick={() => save("worldwide")} className="orbit-chip !justify-start text-xs" data-testid="country-filter-worldwide">🌍 Worldwide</button>
          {shown.map((c) => (
            <button key={c.code} onClick={() => save(c.name)} className="orbit-chip !justify-start text-xs" data-testid={`country-filter-${c.code.toLowerCase()}`}>{c.flag} {c.name}</button>
          ))}
        </div>
      )}
    </span>
  );
}

const GENDER_LABELS = { any: "Everyone", male: "Men only", female: "Women only", nonbinary: "Non-binary only" };

function GenderFilter({ user, onSaved }) {
  const nav = useNavigate();
  const isPremium = !!user.premium || user.role === "admin" || user.role === "moderator";
  const current = user.gender_preference || "any";
  const save = async (value) => {
    try {
      const { data } = await api.post("/preferences", { gender_preference: value });
      onSaved(data);
      toast.success(`Matching with ${GENDER_LABELS[value].toLowerCase()}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!isPremium) {
    return (
      <button onClick={() => nav("/pricing")} className="inline-flex items-center gap-1.5 text-slate-300 hover:text-fuchsia-300" data-testid="dashboard-gender-filter-locked">
        Everyone <Lock className="w-3 h-3 text-fuchsia-300" /> <span className="text-[10px] font-mono text-fuchsia-300">VIP</span>
      </button>
    );
  }
  return (
    <span className="inline-flex flex-wrap gap-1.5" data-testid="dashboard-gender-filter">
      {Object.entries(GENDER_LABELS).map(([k, label]) => (
        <button key={k} onClick={() => save(k)} className={`orbit-chip text-xs ${current === k ? "active" : ""}`} data-testid={`gender-filter-${k}`}>{label}</button>
      ))}
    </span>
  );
}
